import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { NavigationFunction as RootNavigation, Pages } from "../../../Navigation/Routers/root"
import { useTranslation } from 'react-i18next';
import { Button, Modal, PageHeader, FeedCurveViewer, FeedCurveCard, InputField, ButtonList } from '../../../Components';
import { colors, css, fonts, frames, glyphs, mediaQueriesNumbers, useMediaQuery } from '../../../Styles';

import { useAppSelector } from '../../../Store';
import Graph_QL, { useFeedComponents } from '../../../Services/Agrisys_GqlDataCaller';
import { useFeedCurves } from '../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/useFeedCurves';
import { ButtonTypes, ListStyle_Types } from '../../../Definitions';
import { InputField_Handles } from '../../../Components/BaseComponents/input';

export default function FeedCurves() {
  const {t} = useTranslation()
  const navigator = useNavigate()
  const [selectedFeedcurve, setSelectedFeedcurve] = useState<number|undefined>(undefined)
  const [feedcurveModal, setFeedcurveModal] = useState<boolean>(false)
  const [copyFeedCurveMode, setCopyFeedCurveMode] = useState<boolean>(false)
  const [addFeedcurveModal, setAddFeedcurveModal] = useState<boolean>(false)
  const [copyFeedcurveModal, setCopyFeedcurveModal] = useState<boolean>(false)
  const hideBtnText = useMediaQuery(`(max-width: 450px)`);
  const hideBtns = useMediaQuery(`(max-width: 600px)`);
  const mobileXs = useMediaQuery(`(max-width: 690px)`);

  // #region | Data
  const farm = useAppSelector(({activeData}) => activeData.farm)
  const domain = Graph_QL.ObjToString({domain: farm ?? ""});
  const connection = Graph_QL.create({domain: farm ?? ""});

  const {call: getFeedCurvesInfo, data: feedCurvesState, createFeedCurve } = useFeedCurves(connection);
  const feedCurveNameInputRef = useRef<InputField_Handles>(null); 
  
  const feedCurvesData = useMemo(() => {
    return feedCurvesState && feedCurvesState[domain] ? feedCurvesState[domain].data : [];
  },[domain, feedCurvesState])
  
  const feedCurvesIds = useMemo(() => {
    return feedCurvesData.map((feedCurve) => feedCurve.id)
  },[feedCurvesData])

  useEffect(() => {
    if (!feedCurvesState[domain]) {
      getFeedCurvesInfo(connection, {fetchAmount: 50})
    }
  },[connection, domain, feedCurvesState, getFeedCurvesInfo])

  const {call: getFeedComponentsInfo, data: feedComponentsState} = useFeedComponents(connection);

  const componentIds = useMemo(() => {
    return feedCurvesData.flatMap((feedCurve) => feedCurve.mixture).map((mixture) => mixture.component).filter((value, index, self) => self.indexOf(value) === index);
  },[feedCurvesData]);

  const feedComponents = useMemo(() => {
    const feedComponents = feedComponentsState && feedComponentsState[domain] ? feedComponentsState[domain].data : [];
    return feedComponents.filter((feedComponent) => componentIds.includes(feedComponent.id));
  },[componentIds, domain, feedComponentsState])

  useEffect(() => {
    if (!feedComponentsState[domain] && feedCurvesState[domain]) {
      getFeedComponentsInfo(connection, {ids: componentIds});
    }
  },[componentIds, connection, domain, feedComponents.length, feedComponentsState, feedCurvesState, getFeedComponentsInfo])
  // #endregion

  // Save current FeedCurveId and selected FeedCurveData to const
  const currentFeedCurveId = () => feedCurvesIds.indexOf(selectedFeedcurve!);
  const selectedFeedCurveData = feedCurvesData.find(feedCurve => feedCurve.id === selectedFeedcurve);

  // Function to handle navigation between FeedCurves in popup Modal with looping
  const navigateFeedCurves = (direction: number) => {
    const currentIndex = currentFeedCurveId();
    if (currentIndex !== -1) {
      const newIndex = (currentIndex + direction + feedCurvesIds.length) % feedCurvesIds.length;
      setSelectedFeedcurve(feedCurvesIds[newIndex]);
    }
  };

  // Navigate to next FeedCurve in popup Modal
  const goToNext = () => { navigateFeedCurves(1) }
  // Navigate to previous FeedCurve in popup Modal
  const goToPrevious = () => { navigateFeedCurves(-1) }

  // Select Feed Curve Handler
  const onFeedCurveSelect = (id:number) => {
    mobileXs ? RootNavigation(navigator, Pages.feedCurves, Pages.feedCurve, true, {selected:selectedFeedCurveData?.id})
    : setSelectedFeedcurve(id)
    copyFeedCurveMode
    ? setCopyFeedcurveModal(true)
    : setFeedcurveModal(true)
  }

  // Handle modal close
  const handleCloseBtn = () => {
    setAddFeedcurveModal(false) 
    setSelectedFeedcurve(undefined)
    if (copyFeedCurveMode) {
      setCopyFeedcurveModal(false);
    }
  }

  return (
    <frames.Page_inner>
      <frames._page_inner_header>
        <div style={{display: "flex", flexDirection: "row", alignItems: "start", justifyContent: "space-between"}}>
          <PageHeader topHeader={t("globalMenu.feedCurves")} subHeader={t("overview")}/>
          <div style={{display: "block", zIndex: "999"}}>
            <ButtonList
              contents={[copyFeedCurveMode ? t("buttons.cancelBtn") : t("feed.feedCurve.copyFeedcurve")]} 
              onClick={(i => {
                if (i === 0) {setCopyFeedCurveMode(copyFeedCurveMode ? false : true)}
              })}
              buttonType={ButtonTypes.primaryButton}
              overrides={{
                baseButton: { ...( !hideBtns ? {...css.button_FitContent, minWidth: "none"} : {backgroundColor: colors.greyDarkSecondary})}
              }}
              listOverrides={{
                listStyle: !hideBtns ? ListStyle_Types.horizontal : ListStyle_Types.vertical,
              }}
              responsiveOverrides={{
                isResponsive: !hideBtns ? false : true,
                popoverStyle: { backgroundColor: colors.greyDarkPrimary, padding: "20px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px"+colors.blackPrimary+"4d" },
                openContent: hideBtnText ? "" : "Options"
              }}              
            />
          </div>
        </div>
      </frames._page_inner_header>

      <frames._page_inner_content>
        {/* Wrap of Feedcurve cards with miniature feedCurveViewer */}
        <frames._page_inner_content_scroll>
          <frames.CardsColumnCount>
            {/* Create Report Btn */}
            <Button 
              content={t("feed.feedCurve.createFeedCurve")} 
              subContent={t("feed.feedCurve.createFeedCurveFromScratch")} 
              onClick={() => setAddFeedcurveModal(true)}
              buttonType={ButtonTypes.cardButton}
            />
          {/* Backdrop filter */}
          {copyFeedCurveMode
            ? <div style={{...css.backdropFilter}}/>
            : undefined } 
          {/* Feed Curves */}
          {feedCurvesData.map((feedCurve) => (
            <FeedCurveCard key={feedCurve.id} feedCurve={feedCurve} feedComponents={feedComponents} onFeedCurveSelect={onFeedCurveSelect} feedCurveId={feedCurve.id} />
          ))}
          </frames.CardsColumnCount>
        </frames._page_inner_content_scroll>
      </frames._page_inner_content>
      {/* Popup Modal for clicked feedcurve */}
      <Modal
        isModalOpen={!!feedcurveModal}
        overrides={{
          isPrimaryComponent: true,
          root: { padding: "40px 5px 5px 5px" },
          background: colors.greyLightTertiary
        }}
        modalHeader={`${selectedFeedCurveData?.name}`}
        modalSubHeader={t("feed.feedPlan.feedCurve") + ` ${selectedFeedcurve}`}
        modalHeaderContent={
          <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "10px"}}>
            <Button 
              content={t("buttons.prev") + " " + t("feed.feedPlan.feedCurve")}
              startEnhancer={glyphs.Arrow({rotate:180})}
              buttonType={ButtonTypes.textButton}
              onClick={goToPrevious}
              overrides={{
                baseButton: {...fonts.fontSize4}
              }}
            />
            <div style={{backgroundColor: colors.blueDarkSecondary, height: "20px", width: "1.5px"}}/>
            <Button 
              content={t("buttons.next") + " " + t("feed.feedPlan.feedCurve")}
              endEnhancer={glyphs.Arrow({})}
              buttonType={ButtonTypes.textButton}
              onClick={goToNext}
              overrides={{
                baseButton: {...fonts.fontSize4}
              }}
            />
          </div>
        }
        onCloseBtn={() => { setFeedcurveModal(false) }}
      >
        {/* FeedCurve Modal content */}
        <div style={{backgroundColor: colors.greyLightTertiary, borderRadius: "0px 0px 10px 10px", padding: "0px 20px 30px 20px"}}>
        
          {/* FeedCurveViewer */}
          {selectedFeedCurveData && (
            <FeedCurveViewer data={selectedFeedCurveData} feedComponents={feedComponents} showComponentLabels={true} showMilestoneLabels={true}/>
          )}

          {/* Show more FeedCurve details btn */}
          <div style={{margin: "20px auto 0px auto", width: "100%", textAlign: "center"}}>
            <Button
              content={t("feed.feedPlan.feedCurve") + " " + t("buttons.details")}
              onClick={() => {
                RootNavigation(navigator, Pages.feedCurves, Pages.feedCurve, true, {selected:selectedFeedCurveData?.id})
              }}
              buttonType={ButtonTypes.primaryButton}
            />
          </div>
        </div>
      </Modal>
      {/* Add / Copy Feed Curve Modal */}
      <Modal
        isModalOpen={!!addFeedcurveModal||!!copyFeedcurveModal}
        overrides={{ 
          isPrimaryComponent: true 
        }}
        modalHeader={t("feed.feedCurve.newFeedcurve")}
        modalSubHeader={copyFeedcurveModal ? t("feed.feedCurve.fromFeedcurve")+" "+selectedFeedcurve+": "+selectedFeedCurveData?.name : "" }
        onCloseBtn={handleCloseBtn} 
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "40px"}}>
          <div>
            <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 2px 22px" }}>{t("feed.feedCurve.feedcurveName")}</p>
            <InputField textStart={true} ref={feedCurveNameInputRef}/>
          </div>
          <Button
              content={t("buttons.createBtn")+" "+t("feed.feedPlan.feedCurve")}
              onClick={() => {if (feedCurveNameInputRef.current?.context) {createFeedCurve(feedCurveNameInputRef.current.context).then((result) => {if (result.success) {RootNavigation(navigator, Pages.feedCurves, Pages.feedCurve, true, {selected: result.feedcurve_id})}})}}}
              buttonType={ButtonTypes.primaryButton}
              overrides={{
                baseButton: {...css.button_FitContent}
              }}
            />
        </div>
      </Modal>
    </frames.Page_inner>
  );
}  